import React from 'react';
import { Avatar, AvatarSize } from '@/modules/Avatar';
import { LegacyIcon } from '@/modules/LegacyIcon';
import type { LegacyIconName } from '@/shared/types/legacyIcon';
import type { LegacyVisualSlotType, LegacyUrlString } from '@/shared/types/legacyButton';
import { IconFontColor, ButtonColors } from '@/shared/types/types';
import { cn } from '@/shared/utils/cn';
import { getIconFontColor } from '@/modules/InnerButton/utils/colorUtils';
import { isLikelyUrl } from '../utils/legacySlotUrl';

function isLegacyUrl(slot: LegacyVisualSlotType): slot is LegacyUrlString {
  return typeof slot === 'string' && isLikelyUrl(slot);
}

export interface LegacyButtonSlotLeftProps {
  leftSlot?: LegacyVisualSlotType;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  color?: ButtonColors;
  colorIcon?: IconFontColor | IconFontColor;
  className?: string;
  disabled?: boolean;
  strokeWidth?: number;
  onClickLeftSlot?: (e: React.MouseEvent<HTMLDivElement>) => void;
  leftSlotDuotonePrimary?: IconFontColor;
  leftSlotDuotoneSecondary?: IconFontColor;
  leftSlotDuotoneOpacityPrimary?: number;
  leftSlotDuotoneOpacitySecondary?: number;
  hasChildren: boolean;
}

export const LegacyButtonSlotLeft: React.FC<LegacyButtonSlotLeftProps> = ({
  leftSlot,
  size,
  color,
  colorIcon,
  className,
  disabled,
  onClickLeftSlot,
  leftSlotDuotonePrimary,
  leftSlotDuotoneSecondary,
  leftSlotDuotoneOpacityPrimary,
  leftSlotDuotoneOpacitySecondary,
  hasChildren,
}) => {
  if (!hasChildren || !leftSlot) {
    return null;
  }

  const iconColor = getIconFontColor(color, colorIcon);
  const legacySize = size === 'xxs' ? 'xs' : 'sm';
  const resolvedAvatarSize = 'xxs' as AvatarSize;

  if (isLegacyUrl(leftSlot)) {
    return <Avatar type="people" src={leftSlot} className={className} size={resolvedAvatarSize} />;
  }

  return (
    <LegacyIcon
      name={leftSlot as LegacyIconName}
      size={legacySize}
      color={iconColor as IconFontColor}
      className={cn(className, color === 'primary' && disabled && '!ui-text-on-color')}
      onClick={onClickLeftSlot}
      colorDuotonePrimary={leftSlotDuotonePrimary}
      colorDuotoneSecondary={leftSlotDuotoneSecondary}
      opacityDuotonePrimary={leftSlotDuotoneOpacityPrimary}
      opacityDuotoneSecondary={leftSlotDuotoneOpacitySecondary}
    />
  );
};

LegacyButtonSlotLeft.displayName = 'LegacyButtonSlotLeft';

export interface LegacyButtonSlotRightProps {
  rightSlot?: LegacyVisualSlotType;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  color?: ButtonColors;
  colorIcon?: IconFontColor | IconFontColor;
  className?: string;
  disabled?: boolean;
  strokeWidth?: number;
  onClickRightSlot?: (e: React.MouseEvent<HTMLDivElement>) => void;
  rightSlotDuotonePrimary?: IconFontColor;
  rightSlotDuotoneSecondary?: IconFontColor;
  rightSlotDuotoneOpacityPrimary?: number;
  rightSlotDuotoneOpacitySecondary?: number;
  hasChildren: boolean;
}

export const LegacyButtonSlotRight: React.FC<LegacyButtonSlotRightProps> = ({
  rightSlot,
  size,
  color,
  colorIcon,
  className,
  disabled,
  onClickRightSlot,
  rightSlotDuotonePrimary,
  rightSlotDuotoneSecondary,
  rightSlotDuotoneOpacityPrimary,
  rightSlotDuotoneOpacitySecondary,
  hasChildren,
}) => {
  if (!hasChildren || !rightSlot) {
    return null;
  }

  const iconColor = getIconFontColor(color, colorIcon);
  const legacySize = size === 'xxs' ? 'xs' : 'sm';
  const resolvedAvatarSize = 'xxs' as AvatarSize;

  if (isLegacyUrl(rightSlot)) {
    return <Avatar type="people" src={rightSlot} className={className} size={resolvedAvatarSize} />;
  }

  return (
    <LegacyIcon
      name={rightSlot as LegacyIconName}
      size={legacySize}
      color={iconColor as IconFontColor}
      className={cn(className, color === 'primary' && disabled && '!ui-text-on-color')}
      onClick={onClickRightSlot}
      colorDuotonePrimary={rightSlotDuotonePrimary}
      colorDuotoneSecondary={rightSlotDuotoneSecondary}
      opacityDuotonePrimary={rightSlotDuotoneOpacityPrimary}
      opacityDuotoneSecondary={rightSlotDuotoneOpacitySecondary}
    />
  );
};

LegacyButtonSlotRight.displayName = 'LegacyButtonSlotRight';
